import type { EventBus } from '@/core/EventBus';
import { UNIT_DEFINITIONS, getUnitsByTier, type UnitDefinition } from '@/units/UnitDefinitions';

/**
 * Unit pool events
 */
export const PoolEvents = {
  POOL_INITIALIZED: 'pool:initialized',
  UNIT_TAKEN: 'pool:unitTaken',
  UNIT_RETURNED: 'pool:unitReturned',
  UNIT_DEPLETED: 'pool:unitDepleted',
} as const;

/**
 * Default pool copies per tier (TFT-style)
 */
export const DEFAULT_POOL_COPIES: Record<number, number> = {
  1: 13, // Tier 1: 13 copies each
  2: 9, // Tier 2: 9 copies each
  3: 6, // Tier 3: 6 copies each
  4: 4, // Tier 4: 4 copies each
  5: 2, // Tier 5: 2 copies each
};

/**
 * Star level to copies mapping
 * When selling a unit, return copies based on star level
 */
export const STAR_LEVEL_COPIES: Record<number, number> = {
  1: 1, // 1★ = 1 copy
  2: 2, // 2★ = 2 copies (made from 2 units, but we give back 2 not 3)
  3: 4, // 3★ = 4 copies
  4: 8, // 4★ = 8 copies
};

/**
 * Shared unit pool system
 * Both players draw from the same limited pool of unit copies.
 * Buying depletes the pool; selling returns copies.
 */
export class UnitPool {
  private eventBus: EventBus;
  private pool: Map<string, number> = new Map();
  private poolCopies: Record<number, number>;

  constructor(eventBus: EventBus, poolCopies: Record<number, number> = DEFAULT_POOL_COPIES) {
    this.eventBus = eventBus;
    this.poolCopies = poolCopies;
    this.initialize();
  }

  /**
   * Initialize the pool with all unit definitions
   */
  initialize(): void {
    this.pool.clear();

    for (const [unitType, definition] of Object.entries(UNIT_DEFINITIONS)) {
      const copies = this.poolCopies[definition.tier] ?? this.poolCopies[1];
      this.pool.set(unitType, copies);
    }

    this.eventBus.emit(PoolEvents.POOL_INITIALIZED, {
      totalUnits: this.pool.size,
      poolState: this.getPoolState(),
    });
  }

  /**
   * Take a unit from the pool
   * @returns true if successful, false if no copies available
   */
  take(unitType: string): boolean {
    const remaining = this.pool.get(unitType);

    if (remaining === undefined || remaining <= 0) {
      return false;
    }

    const newCount = remaining - 1;
    this.pool.set(unitType, newCount);

    this.eventBus.emit(PoolEvents.UNIT_TAKEN, {
      unitType,
      remaining: newCount,
    });

    if (newCount === 0) {
      this.eventBus.emit(PoolEvents.UNIT_DEPLETED, {
        unitType,
      });
    }

    return true;
  }

  /**
   * Return units to the pool (when selling)
   * @param unitType The unit type to return
   * @param starLevel The star level of the sold unit (determines copies returned)
   */
  return(unitType: string, starLevel: number): void {
    const current = this.pool.get(unitType) ?? 0;
    const copiesToReturn = STAR_LEVEL_COPIES[starLevel] ?? 1;
    const definition = UNIT_DEFINITIONS[unitType];

    // Cap at the max pool size for this tier
    const maxCopies = definition ? (this.poolCopies[definition.tier] ?? this.poolCopies[1]) : this.poolCopies[1];
    const newCount = Math.min(current + copiesToReturn, maxCopies);

    this.pool.set(unitType, newCount);

    this.eventBus.emit(PoolEvents.UNIT_RETURNED, {
      unitType,
      copiesReturned: copiesToReturn,
      remaining: newCount,
    });
  }

  /**
   * Get remaining copies of a unit type
   */
  getRemaining(unitType: string): number {
    return this.pool.get(unitType) ?? 0;
  }

  /**
   * Get all available units of a specific tier (copies > 0)
   */
  getAvailableUnitsOfTier(tier: number): UnitDefinition[] {
    const tierUnits = getUnitsByTier(tier);
    return tierUnits.filter((unit) => this.getRemaining(unit.unitType) > 0);
  }

  /**
   * Check if a unit type is available
   */
  isAvailable(unitType: string): boolean {
    return this.getRemaining(unitType) > 0;
  }

  /**
   * Reset the pool for a new match
   */
  reset(): void {
    this.initialize();
  }

  /**
   * Get the current pool state (for debugging/UI)
   */
  getPoolState(): Record<string, number> {
    const state: Record<string, number> = {};
    for (const [unitType, count] of this.pool) {
      state[unitType] = count;
    }
    return state;
  }

  /**
   * Sync pool state from server (for multiplayer)
   * @param serverState The pool state from the server
   */
  syncFromServer(serverState: Record<string, number>): void {
    this.pool.clear();
    for (const [unitType, count] of Object.entries(serverState)) {
      this.pool.set(unitType, count);
    }
  }
}
